import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { InvoiceSettingsRow, PaymentMethod, Shortcut } from "@/types";

export function usePaymentMethods() {
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("payment_methods")
        .select("id, name, code, requires_reference, active, created_at")
        .eq("active", true)
        .order("name");
      if (error) {
        setMethods([]);
        return;
      }
      setMethods((data ?? []) as PaymentMethod[]);
    } catch {
      setMethods([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { methods, loading, reload: load };
}

export function useShortcuts() {
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("shortcuts")
        .select("id, key_code, action, description")
        .order("key_code");
      if (error) {
        setShortcuts([]);
        return;
      }
      setShortcuts((data ?? []) as Shortcut[]);
    } catch {
      setShortcuts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { shortcuts, loading, reload: load };
}

export function useInvoiceSettings() {
  const [settings, setSettings] = useState<InvoiceSettingsRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("invoice_settings")
        .select(
          "id, doc_type, show_logo, show_rnc, show_phone, show_address, show_cashier, show_customer, show_discount, show_tax, show_ncf, show_social, show_return_message, show_thanks, logo_size, header_text, footer_text, thanks_text, return_text",
        );
      if (error) {
        setSettings([]);
        return;
      }
      setSettings((data ?? []) as InvoiceSettingsRow[]);
    } catch {
      setSettings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const posSettings = settings.find((s) => s.doc_type === "pos") ?? null;
  const a4Settings = settings.find((s) => s.doc_type === "a4") ?? null;

  return { settings, posSettings, a4Settings, loading, reload: load };
}

export interface PrinterConfig {
  pos_printer: string | null;
  pos_width: number | null;
  a4_printer: string | null;
  auto_print: boolean | null;
  open_drawer: boolean | null;
  copies: number | null;
}

export function usePrinterSettings() {
  const [config, setConfig] = useState<PrinterConfig>({
    pos_printer: null,
    pos_width: 80,
    a4_printer: null,
    auto_print: false,
    open_drawer: false,
    copies: 1,
  });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("printer_settings")
          .select("pos_printer, pos_width, a4_printer, auto_print, open_drawer, copies")
          .limit(1)
          .maybeSingle();
        if (!error && data && active) {
          setConfig({
            pos_printer: data.pos_printer,
            pos_width: data.pos_width ?? 80,
            a4_printer: data.a4_printer,
            auto_print: data.auto_print ?? false,
            open_drawer: data.open_drawer ?? false,
            copies: data.copies ?? 1,
          });
        }
      } catch {
        // Keep defaults.
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return config;
}